<template>
	<div class="change-password">
		<div class="change-password__title _md">{{ $t('Change Password') }}</div>
		<form class="change-password__form" @submit.prevent="resetPassword">
			<div class="change-password__inputs">
				<BaseInput
					class="change-password__input"
					v-model="passwordData.currentPassword"
					type="password"
					:label="`${$t('Current password')}:`"
				/>
				<BaseInput
					class="change-password__input"
					v-model="passwordData.newPassword"
					type="password"
					:label="`${$t('New password')}:`"
				/>
				<BaseInput
					class="change-password__input"
					v-model="passwordData.confirmPassword"
					type="password"
					:label="`${$t('Confirm new password')}:`"
				/>
			</div>
			<p v-if="errorMessage" class="change-password__error-message">
				{{ errorMessage }}
			</p>
			<p v-if="successMessage" class="change-password__success-message">
				{{ successMessage }}
			</p>
			<BaseButton
				class="change-password__submit _btn-bordered"
				type="submit"
				:disabled="isSubmitting"
			>
				<span v-if="isSubmitting">{{ $t('Changing') }}...</span>
				<span v-else>{{ $t('Save Changes') }}</span>
			</BaseButton>
		</form>
	</div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';

export default {
	name: 'PasswordReset',
	components: { BaseInput, BaseButton },
	data() {
		return {
			isSubmitting: false,
			passwordData: {
				currentPassword: '',
				newPassword: '',
				confirmPassword: '',
			},
			errorMessage: '', // Store error messages from API
			successMessage: '', // Store success messages
		};
	},
	mounted() {
		this.isSubmitting = false;
	},
	methods: {
		async resetPassword() {
			this.isSubmitting = true;
			this.errorMessage = ''; // Clear previous errors
			this.successMessage = ''; // Clear success message

			try {
				const response = await this.$http.post(
					'user/change-password',
					{
						cur_password: this.passwordData.currentPassword,
						new_password: this.passwordData.newPassword,
						repeat_password: this.passwordData.confirmPassword,
					}
				);

				// If the API response status is OK, display success
				if (response.data.status === 'OK') {
					this.successMessage = this.$t('Password changed successfully!');
					this.clearForm();
					this.isSubmitting = false;
				}
			} catch (error) {
				if (error.response && error.response.data) {
					// Capture and display the API error message directly
					this.errorMessage =
						this.$t(error.response.data.message) ||
						'An unexpected error occurred.';
					this.isSubmitting = false;
				}
			}
		},
		clearForm() {
			this.passwordData.currentPassword = '';
			this.passwordData.newPassword = '';
			this.passwordData.confirmPassword = '';
		},
	},
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.change-password {
	flex: 0 1 calc(50% - 10px);
	background-color: var(--bg-blur-card);
	border-radius: var(--border-radius-primary);
	display: flex;
	flex-direction: column;
	@media (max-width: $md2) {
		flex: 0 1 calc(50% - 5px);
	}
	@media (max-width: $md3) {
		flex: 1 1 100%;
		max-width: 400px;
	}
	&__title {
		text-transform: uppercase;
		text-align: center;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 15, 10);
		}
	}
	&__form {
		flex: 1 1 100%;
		display: flex;
		flex-direction: column;
	}
	&__inputs {
		flex: 1 1 100%;
	}
	&__info {
		text-align: center;
		color: var(--secondary-color);
		font-weight: 400;
		display: block;
		&:not(:last-child) {
			@include adaptiveValue('padding-top', 10, 0, 1470, 992, 1);
			@include adaptiveValue('margin-bottom', 20, 10, 1470, 992, 1);
		}
	}
	&__input {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 15, 10);
		}
	}
	&__submit {
		@include adaptiveValue('margin-top', 40, 15);
		width: 100%;
	}
	&__success-message,
	&__error-message {
		text-align: center;
		margin-bottom: 8px;
	}
	&__error-message {
		color: var(--error-color);
	}
	&__success-message {
		color: var(--success-color);
	}
}
</style>
